import React, { memo, useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'
import styled from '@emotion/styled'
import { DatePicker } from '@mantine/dates'
import { IconX } from '@tabler/icons'
import { Button, DatePickerRange } from '@ow/comps'
import locale from '@ow/comps/locale/zh-CN'
import { dayjsInit } from '@ow/utils'
import { IconMaps } from '@ow/icons'
import { useAppEx } from '@vt/hooks'
import Avatar from '../avatar'
import Select from './select'
import Search from './search'
import Exporter from './export'
import { useCustomizedCheck } from '../config'
import 'dayjs/locale/zh-cn'

const today = new Date()

const Container = styled.div`
  padding: 20px 0;
  width: ${(props) => (props.isMobile ? '100%' : '336px')};
  height: 100%;
  background: #fff;
  position: ${(props) => (props.isMobile ? 'absolute' : 'relative')};
  left: 0;
  top: ${(props) => (!props.isMobile || props.visible ? '0' : '100%')};
  transition: top 0.3s ease-in-out;
  z-index: 100;
  .status-tab {
    margin-top: 8px;
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    background: #e7e7e7;
    height: ${(props) => (props.isMobile ? '32px' : '40px')};
    border-radius: 4px;
  }

  .status {
    flex: 1;
    display: flex;
  }
  .status-title {
    width: ${(props) => (props.isMobile ? '31%' : '30%')};
    text-align: center;
    height: ${(props) => (props.isMobile ? '28px' : '28px')};
    line-height: ${(props) => (props.isMobile ? '28px' : '28px')};
    border-radius: 4px;
    cursor: pointer;
  }

  .active-status {
    background: #fff;
  }

  .line {
    width: 1px;
    height: 18px;
    background: #d6dbe3;
  }

  .user-info {
    /* background: #d8d8d8; */
    height: calc(100% - 182px);
    overflow: auto;
  }
`

const datePickerStyle = {
  width: '100%',
}

const getDatePickerProps = (isMobile) =>
  isMobile
    ? {
        radius: 'md',
        size: 'xs',
        className: 'h-[32px]',
        dropdownType: 'modal',
        modalProps: {
          fullScreen: true,
          styles: { root: { marginTop: '160px' } },
        },
      }
    : {}

const STATUS = [
  { title: '全部', key: 'all' },
  { title: '在线', key: 'online' },
  { title: '离线', key: 'offline' },
]

// 用户信息
const User = ({ user = {}, onSelect, selected }) => {
  const { name, online, lastInspectTime } = user
  return (
    <div
      className={`flex flex-row items-center select-none cursor-pointer h-14 px-5 ${
        selected && 'bg-blue-50'
      }`}
      onClick={() => {
        onSelect(selected ? null : user)
      }}
    >
      <Avatar user={user} />
      <div className="h-10 ml-2 flex-1 flex flex-col justify-center">
        <div
          className="flex justify-start items-center text-gray-900 dark:text-gray-100 leading-5"
          style={{ fontSize: '14px' }}
        >
          {name || '暂无用户名称'}
        </div>
        <div className="text-gray-400 text-xs">
          最后上报时间：
          {lastInspectTime
            ? dayjsInit(lastInspectTime).format('MM-DD HH:mm')
            : '暂无'}
        </div>
      </div>
      <div className="flex items-center">
        {online ? (
          <IconMaps type="geo" size={20} />
        ) : (
          <IconMaps type="geo" size={20} fill="#00000066" />
        )}
      </div>
    </div>
  )
}

const LayerHandle = ({
  userList = [],
  curDate,
  onTimeChange,
  selected,
  onSelect,
  filter,
  isMobile,
}) => {
  const { tbs } = useAppEx()
  const config = useCustomizedCheck()
  const signInTb = config.export
    ? Object.values(tbs).find((tb) => tb.tbSlug === config.export.tbSlug)
    : null
  const signInVid = config.export?.vid

  const [curStatus, setStatus] = useState(0)
  const [search, setSearch] = useState([])
  const [keyword, setKeyword] = useState()
  const [visible, setVisible] = useState(false)

  const onUserSelect = (u) => {
    onSelect(u)
    if (isMobile) {
      setVisible(false)
    }
  }

  const onTimeRangeChange = useCallback((range) => {
    const { startDate, endDate } = range[0]
    if (startDate && endDate) {
      onTimeChange([startDate, endDate])
    }
  }, [onTimeChange])

  const timeRange = useMemo(() => {
    if (!Array.isArray(curDate)) return null
    const [startDate, endDate] = curDate
    return [{ startDate, endDate }]
  }, [curDate])

  const userAfterFilter = useMemo(() => {
    switch (STATUS[curStatus].key) {
      case 'online':
        return userList.filter((u) => u.online)
      case 'offline':
        return userList.filter((u) => !u.online)
      default:
        return userList
    }
  }, [userList, curStatus])

  const userAfterSearch = useMemo(() => {
    if (search.length) {
      return userAfterFilter.filter((u) => search.includes(u.id))
    }
    if (keyword) {
      return userAfterFilter.filter((u) => u.name.includes(keyword))
    }
    return userAfterFilter
  }, [userAfterFilter, search, keyword])

  const userAfterSort = useMemo(() => {
    return userAfterSearch
      .sort((x, y) => {
        return x.name.localeCompare(y.name, 'zh')
      })
      .sort((x, y) => {
        return y.online - x.online
      })
  }, [userAfterSearch])

  return (
    <>
      <Container isMobile={isMobile} visible={visible}>
        <div className={clsx(isMobile ? 'px-3' : 'px-5')}>
          <div className="flex gap-2 items-center mb-2">
            {isMobile ? (
              <Search value={keyword} onChange={setKeyword} />
            ) : (
              <Select userList={userAfterFilter} onSelect={setSearch} />
            )}
            {isMobile &&
              (keyword ? (
                <Button
                  type="primary"
                  opacityType="transparent"
                  style={{ paddingRight: 0, paddingLeft: '6px' }}
                  onClick={() => setKeyword('')}
                >
                  取消
                </Button>
              ) : (
                <div
                  className="text-center pl-3"
                  onClick={() => setVisible(false)}
                >
                  <IconX />
                </div>
              ))}
            {signInTb && (
              <Exporter
                curDate={curDate}
                useList={userAfterSearch}
                selectedUser={selected}
                filter={filter}
                tbId={signInTb.tbId}
                vid={signInVid}
              />
            )}
          </div>

          <div className="relative">
            {config.dateRangePicker ? (
              <DatePickerRange
                locale={locale}
                ranges={timeRange}
                inputStyle={datePickerStyle}
                placeholder="请选择日期"
                onChange={onTimeRangeChange}
                showTime
              />
            ) : (
              <DatePicker
                locale="zh-cn"
                placeholder="请选择日期"
                inputFormat="YYYY/MM/DD"
                labelFormat="YYYY/MM"
                onChange={onTimeChange}
                maxDate={today}
                value={curDate}
                clearable={false}
                allowSingleDateInRange
                {...getDatePickerProps(isMobile)}
              />
            )}
            <div className="absolute right-2 bottom-2 pointer-events-none">
              <IconMaps type="calendar" size={20} />
            </div>
          </div>
          <div className="status-tab">
            {STATUS.map((e, i) => {
              return (
                <React.Fragment key={e.key}>
                  <div
                    className={`status-title ${
                      curStatus === i && 'active-status'
                    }`}
                    onClick={() => {
                      setStatus(i)
                    }}
                  >
                    {e.title}
                  </div>
                  {i !== STATUS.length - 1 && <div className="line" />}
                </React.Fragment>
              )
            })}
          </div>
          <div className="bg-blue-50 h-9 leading-5 rounded p-2 my-4 text-xs">
            {STATUS[curStatus].title}检查人员：{userAfterFilter.length}人
          </div>
        </div>
        <div className="user-info">
          {userAfterSort.map((e) => {
            return (
              <User
                key={e.id}
                user={e}
                onSelect={onUserSelect}
                selected={selected?.id === e.id}
              />
            )
          })}
        </div>
      </Container>
      {isMobile && (
        <button
          className="fixed z-[50] right-4 bottom-[60px] w-[50px] h-[50px] bg-white rounded-full flex items-center justify-center shadow focus:opacity-80"
          onClick={() => setVisible(!visible)}
          type="button"
        >
          <IconMaps type="users" width={24} height={24} />
        </button>
      )}
    </>
  )
}

export default memo(LayerHandle)
